// assets/marcador-3d-viewer.js
import { Component } from '@theme/component';

/**
 * Line item property keys read from the product-custom-property blocks of the game marker template.
 * Keep them aligned with the `property_key` settings in templates/product.marcador.json.
 */
const PROPERTIES = {
  animal: 'Animal',
  split: 'Reparto de animales',
  silhouette: 'Silueta propia',
  animalColor: 'Color del animal',
  background: 'Color del fondo',
  border: 'Borde',
  borderColor: 'Color del borde',
  initials: 'Iniciales o nombre',
  logo: 'Logotipo',
  markColor: 'Color de las iniciales o del logo',
  caseColor: 'Color del estuche',
  caseInitials: 'Iniciales del estuche',
  caseLettersColor: 'Color de las letras del estuche',
};

const SHAPE_OPTION = /forma/i;
const QUANTITY_OPTION = /cantidad|unidades/i;
const RIBBON_OPTION = /cinta/i;
/** "Sin cinta" and its translations of the same idea: no ribbon on the marker. */
const NO_RIBBON_VALUE = /^(sin|no)\b/i;
const CASE_VALUE = /estuche/i;
/** "Otra (subo mi silueta)": the customer's own silhouette instead of a listed animal. */
const CUSTOM_ANIMAL = /^otr/i;
const FLAG_BORDER = /bander/i;
/** Swatch names of the flag, which uses two filament colors. */
const FLAG_COLORS = ['Rojo', 'Amarillo'];
const FLAG_HEX = { Rojo: '#D2433D', Amarillo: '#F7E40B' };
const MAX_COLORS = 4;
const DEFAULTS = { animal: '#F68A47', background: '#F4F1EC', border: '#2B2B2B', mark: '#2B2B2B', case: '#B0936C', caseLetters: '#2B2B2B' };
const SAMPLE_INITIALS = 'J.M.G.';
// Desktop shows the preview sticky beside the fields; below it the preview floats over the page
const DESKTOP_QUERY = window.matchMedia('(min-width: 750px)');

/** @type {Promise<void> | null} */
let threeLoading = null;

/** Backdrop of the order picture, close to the dark stage behind the preview. */
const PICTURE_BACKDROP = '#15171c';
/** Longest side of the order picture, in pixels. */
const PICTURE_MAX_SIDE = 1200;

/**
 * Copies a freshly rendered WebGL canvas onto an opaque backdrop, scaled down to the order picture size.
 * @param {HTMLCanvasElement} source
 * @returns {HTMLCanvasElement | null}
 */
function pictureOf(source) {
  if (!source.width || !source.height) return null;
  const scale = Math.min(1, PICTURE_MAX_SIDE / Math.max(source.width, source.height));
  const picture = document.createElement('canvas');
  picture.width = Math.round(source.width * scale);
  picture.height = Math.round(source.height * scale);
  const g = picture.getContext('2d');
  if (!g) return null;
  g.fillStyle = PICTURE_BACKDROP;
  g.fillRect(0, 0, picture.width, picture.height);
  g.drawImage(source, 0, 0, picture.width, picture.height);
  return picture;
}

/**
 * Puts a picture in a file field of the product form, so it is uploaded with the order.
 * @param {HTMLCanvasElement} picture
 * @param {HTMLInputElement} input
 */
function attachPicture(picture, input) {
  const [, data = ''] = picture.toDataURL('image/jpeg', 0.85).split(',');
  const bytes = Uint8Array.from(atob(data), (character) => character.charCodeAt(0));
  try {
    const transfer = new DataTransfer();
    transfer.items.add(new File([bytes], 'diseno-3d.jpg', { type: 'image/jpeg' }));
    input.files = transfer.files;
  } catch {
    // Browsers that cannot set files send the order without the picture
  }
}

/**
 * Tells the personalization fields how much of the top of the screen the floating preview covers,
 * so a field with an error is scrolled into view below it (see `--personalization-scroll-offset`).
 * @param {Element | null} stage - The floating stage, or null when nothing floats.
 */
function setScrollOffset(stage) {
  const root = document.documentElement;
  if (stage) root.style.setProperty('--personalization-scroll-offset', `${Math.round(stage.getBoundingClientRect().bottom) + 16}px`);
  else root.style.removeProperty('--personalization-scroll-offset');
}

/**
 * @param {string | undefined} formId
 * @returns {HTMLFormElement | null}
 */
function getForm(formId) {
  const form = document.getElementById(formId ?? '');
  return form instanceof HTMLFormElement ? form : null;
}

/**
 * Returns the controls of a line item property, including those linked with the `form` attribute.
 * @param {HTMLFormElement | null} form
 * @param {string} key
 * @returns {Array<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>}
 */
function getControls(form, key) {
  const field = form?.elements.namedItem(`properties[${key}]`);
  if (!field) return [];
  const items = field instanceof RadioNodeList ? [...field] : [field];
  return items.filter(
    (item) => item instanceof HTMLInputElement || item instanceof HTMLSelectElement || item instanceof HTMLTextAreaElement
  );
}

/**
 * @param {HTMLFormElement | null} form
 * @param {string} key
 * @returns {string} The trimmed value, the checked radio value, or an empty string.
 */
function readValue(form, key) {
  for (const control of getControls(form, key)) {
    if (control instanceof HTMLInputElement && control.type === 'radio') {
      if (control.checked) return control.value.trim();
      continue;
    }
    if (control instanceof HTMLInputElement && control.type === 'file') return control.files?.length ? 'file' : '';
    return control.value.trim();
  }
  return '';
}

/**
 * @param {HTMLFormElement | null} form
 * @param {string} key
 * @returns {string} The hex color of the checked swatch, or an empty string.
 */
function readSwatchColor(form, key) {
  const checked = getControls(form, key).find((control) => control instanceof HTMLInputElement && control.checked);
  return checked instanceof HTMLInputElement ? (checked.dataset.color ?? '') : '';
}

/**
 * @param {HTMLFormElement | null} form
 * @param {string} key
 * @returns {File | null} The image chosen in a file upload property.
 */
function readFile(form, key) {
  const control = getControls(form, key)[0];
  const file = control instanceof HTMLInputElement && control.type === 'file' ? control.files?.[0] : null;
  return file && file.type.startsWith('image/') ? file : null;
}

/**
 * @param {string} name
 * @returns {string} The name without case, accents or spaces, so "Jabalí" and "jabali" match.
 */
function animalKey(name) {
  return name
    .normalize('NFD')
    .replace(/[^a-z]/gi, '')
    .toLowerCase();
}

/**
 * @typedef {object} MarkerLayout
 * @property {'silueta' | 'redondo' | 'cuadrado'} shape
 * @property {number} quantity
 * @property {boolean} withCase
 * @property {boolean} ribbon - The flag ribbon option is selected: the marker gets a ribbon slot and the ribbon.
 */

/**
 * Reads the selected variant: marker shape, quantity (with or without the case) and ribbon.
 * @param {ParentNode} root - The product section.
 * @returns {MarkerLayout}
 */
function readLayout(root) {
  /** @type {MarkerLayout} */
  const layout = { shape: 'silueta', quantity: 1, withCase: false, ribbon: false };
  for (const element of root.querySelectorAll('[data-option-name]:checked')) {
    if (!(element instanceof HTMLInputElement || element instanceof HTMLOptionElement)) continue;
    const name = element.dataset.optionName ?? '';
    if (SHAPE_OPTION.test(name)) {
      layout.shape = /redond/i.test(element.value) ? 'redondo' : /cuadr/i.test(element.value) ? 'cuadrado' : 'silueta';
    } else if (QUANTITY_OPTION.test(name)) {
      layout.quantity = Math.min(Math.max(parseInt(element.value, 10) || 1, 1), 20);
      layout.withCase = CASE_VALUE.test(element.value);
    } else if (RIBBON_OPTION.test(name)) {
      layout.ribbon = !NO_RIBBON_VALUE.test(element.value.trim());
    }
  }
  return layout;
}

/**
 * Parses the split written by the marker options block, e.g. "3 Venado, 2 Jabalí".
 * @param {string} value
 * @returns {Array<[string, number]>}
 */
function parseSplit(value) {
  return [...value.matchAll(/(\d+)\s+([^,]+)/g)].map((match) => [(match[2] ?? '').trim(), Number(match[1])]);
}

/**
 * Filament colors one marker needs, by swatch name.
 * @param {HTMLFormElement | null} form
 * @param {MarkerLayout} layout
 * @returns {string[]}
 */
function colorsUsed(form, layout) {
  const used = new Set();
  const add = (/** @type {string} */ name) => name && used.add(name);
  add(readValue(form, PROPERTIES.animalColor));
  if (layout.shape !== 'silueta') add(readValue(form, PROPERTIES.background));
  // An empty border list still shows the flag, as the 3D preview does
  if (FLAG_BORDER.test(readValue(form, PROPERTIES.border) || 'Bandera')) FLAG_COLORS.forEach(add);
  else add(readValue(form, PROPERTIES.borderColor));
  if (readValue(form, PROPERTIES.initials) || readFile(form, PROPERTIES.logo)) add(readValue(form, PROPERTIES.markColor));
  return [...used];
}

/**
 * @param {string} threeSrc
 * @param {string} controlsSrc
 * @returns {Promise<void>}
 */
function loadThree(threeSrc, controlsSrc) {
  if (window.THREE?.OrbitControls) return Promise.resolve();
  threeLoading ??= loadScript(threeSrc).then(() => loadScript(controlsSrc));
  return threeLoading;
}

/**
 * @param {string} src
 * @returns {Promise<void>}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.append(script);
  });
}

/* Marker proportions. A silhouette is rasterised at SIL_PX on its longest side; its border is a
   dilation of it, so every thin part (antlers, legs) gets a printable rim. */
const MARCADOR_MM = 70;
const SIL_PX = 300;
const BORDE_PX = 12;
const PAD = BORDE_PX + 6;
const MM = MARCADOR_MM / (SIL_PX + 2 * BORDE_PX);
/** Radius of the round hole of the silhouette markers, for the metal ring. */
const AGUJERO_MM = 2.4;
/** Slot for the flag ribbon of the round and square markers. */
const RANURA = { w: 11, h: 2.6 };
const PARED_MM = 2;
const BASE_MM = 2;
const RELIEVE_MM = 1;

/* ------------------------------------------------------------------ image → masks → contours */

async function inkFromSrc(src, maxSide = 640) {
  const image = new Image();
  if (!/^(blob|data):/.test(src)) image.crossOrigin = 'anonymous';
  image.src = src;
  await image.decode();
  const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
  const w = Math.max(1, Math.round(image.naturalWidth * scale));
  const h = Math.max(1, Math.round(image.naturalHeight * scale));
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true });
  g.drawImage(image, 0, 0, w, h);
  const d = g.getImageData(0, 0, w, h).data;
  const lum = (i) => 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
  let transparent = 0;
  for (let i = 3; i < d.length; i += 4) if (d[i] < 200) transparent++;
  const useAlpha = transparent > (d.length / 4) * 0.02;
  let edge = 0;
  for (let x = 0; x < w; x++) edge += lum(x * 4) + lum(((h - 1) * w + x) * 4);
  for (let y = 0; y < h; y++) edge += lum(y * w * 4) + lum((y * w + w - 1) * 4);
  const lightDrawing = edge / (2 * (w + h)) < 128;

  const out = g.createImageData(w, h);
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let i = 0; i < d.length; i += 4) {
    const ink = lightDrawing ? lum(i) : 255 - lum(i);
    const a = useAlpha ? d[i + 3] : Math.max(0, Math.min(255, (ink - 55) * 2.55)) * (d[i + 3] / 255);
    out.data[i + 3] = a;
    if (a > 127) {
      const p = i / 4, x = p % w, y = (p - x) / w;
      if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) throw new Error('Imagen vacía');
  g.putImageData(out, 0, 0);
  return { canvas: c, bbox: { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 } };
}

/** Binary grid of the silhouette, its longest side SIL_PX, framed by PAD empty pixels. */
function rasterize({ canvas, bbox }) {
  const s = SIL_PX / Math.max(bbox.w, bbox.h);
  const W = Math.round(bbox.w * s) + 2 * PAD;
  const H = Math.round(bbox.h * s) + 2 * PAD;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d', { willReadFrequently: true });
  g.imageSmoothingQuality = 'high';
  g.drawImage(canvas, bbox.x, bbox.y, bbox.w, bbox.h, PAD, PAD, W - 2 * PAD, H - 2 * PAD);
  const d = g.getImageData(0, 0, W, H).data;
  const grid = new Uint8Array(W * H);
  for (let i = 0; i < grid.length; i++) grid[i] = d[i * 4 + 3] > 127 ? 1 : 0;
  return { W, H, grid };
}

/** Chamfer (3-4) distance, in pixels, from every pixel to the nearest pixel whose value is `target`. */
function distanceTo(grid, W, H, target) {
  const INF = 1e9;
  const d = new Float32Array(W * H);
  for (let i = 0; i < d.length; i++) d[i] = grid[i] === target ? 0 : INF;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x;
    if (d[i] === 0) continue;
    let v = d[i];
    if (x > 0) v = Math.min(v, d[i - 1] + 3);
    if (y > 0) {
      v = Math.min(v, d[i - W] + 3);
      if (x > 0) v = Math.min(v, d[i - W - 1] + 4);
      if (x < W - 1) v = Math.min(v, d[i - W + 1] + 4);
    }
    d[i] = v;
  }
  for (let y = H - 1; y >= 0; y--) for (let x = W - 1; x >= 0; x--) {
    const i = y * W + x;
    let v = d[i];
    if (x < W - 1) v = Math.min(v, d[i + 1] + 3);
    if (y < H - 1) {
      v = Math.min(v, d[i + W] + 3);
      if (x < W - 1) v = Math.min(v, d[i + W + 1] + 4);
      if (x > 0) v = Math.min(v, d[i + W - 1] + 4);
    }
    d[i] = v;
  }
  for (let i = 0; i < d.length; i++) d[i] /= 3;
  return d;
}

/** Closed outlines of the filled pixels, walked along pixel edges: outer ones clockwise on screen, holes the other way. */
function traceLoops(grid, W, H) {
  const at = (x, y) => (x >= 0 && y >= 0 && x < W && y < H ? grid[y * W + x] : 0);
  const key = (x, y) => y * (W + 1) + x;
  const out = new Map();
  const edges = [];
  const add = (x1, y1, x2, y2) => {
    const e = { x1, y1, x2, y2, dx: x2 - x1, dy: y2 - y1, used: false };
    edges.push(e);
    const k = key(x1, y1);
    if (!out.has(k)) out.set(k, []);
    out.get(k).push(e);
  };
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    if (!at(x, y)) continue;
    if (!at(x, y - 1)) add(x, y, x + 1, y);
    if (!at(x + 1, y)) add(x + 1, y, x + 1, y + 1);
    if (!at(x, y + 1)) add(x + 1, y + 1, x, y + 1);
    if (!at(x - 1, y)) add(x, y + 1, x, y);
  }
  const loops = [];
  for (const first of edges) {
    if (first.used) continue;
    const loop = [[first.x1, first.y1]];
    let cur = first;
    for (;;) {
      cur.used = true;
      if (cur.x2 === first.x1 && cur.y2 === first.y1) break;
      loop.push([cur.x2, cur.y2]);
      const options = (out.get(key(cur.x2, cur.y2)) || []).filter((e) => !e.used);
      if (!options.length) break;
      // At a corner shared by two diagonal pixels, turn right so they stay separate pieces
      const right = [-cur.dy, cur.dx];
      cur = options.find((e) => e.dx === right[0] && e.dy === right[1])
        ?? options.find((e) => e.dx === cur.dx && e.dy === cur.dy)
        ?? options[0];
    }
    if (loop.length > 3) loops.push(loop);
  }
  return loops;
}

function area(points) {
  let s = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    s += x1 * y2 - x2 * y1;
  }
  return s / 2;
}

function simplify(points, epsilon) {
  if (points.length < 3) return points;
  const keep = new Uint8Array(points.length);
  keep[0] = keep[points.length - 1] = 1;
  const stack = [[0, points.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop();
    const [ax, ay] = points[a], [bx, by] = points[b];
    const len = Math.hypot(bx - ax, by - ay) || 1;
    let worst = -1, index = -1;
    for (let i = a + 1; i < b; i++) {
      const [px, py] = points[i];
      const dist = Math.abs((bx - ax) * (ay - py) - (ax - px) * (by - ay)) / len;
      if (dist > worst) { worst = dist; index = i; }
    }
    if (worst > epsilon) { keep[index] = 1; stack.push([a, index], [index, b]); }
  }
  return points.filter((_, i) => keep[i]);
}

/** Simplifies a closed outline: split at the point farthest from the start, since both ends of a closed line coincide. */
function simplifyClosed(points, epsilon) {
  const [x0, y0] = points[0];
  let far = 0;
  points.forEach(([x, y], i) => {
    if (Math.hypot(x - x0, y - y0) > Math.hypot(points[far][0] - x0, points[far][1] - y0)) far = i;
  });
  const first = simplify(points.slice(0, far + 1), epsilon);
  const second = simplify([...points.slice(far), points[0]], epsilon);
  return [...first, ...second.slice(1, -1)];
}

function smoothClosed(points) {
  const out = [];
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    out.push([0.75 * x1 + 0.25 * x2, 0.75 * y1 + 0.25 * y2], [0.25 * x1 + 0.75 * x2, 0.25 * y1 + 0.75 * y2]);
  }
  return out;
}

function inside(point, polygon) {
  const [x, y] = point;
  let hit = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i], [xj, yj] = polygon[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

/** THREE shapes in millimetres, centred on the marker, y up. */
function shapesFrom(grid, W, H) {
  const loops = traceLoops(grid, W, H)
    .map((loop) => smoothClosed(simplifyClosed(loop, 0.8)))
    .filter((loop) => loop.length > 2 && Math.abs(area(loop)) > 8);
  const outers = loops.filter((l) => area(l) > 0).map((l) => ({ loop: l, area: area(l), holes: [] }));
  for (const hole of loops.filter((l) => area(l) < 0)) {
    const owner = outers.filter((o) => inside(hole[0], o.loop)).sort((a, b) => a.area - b.area)[0];
    owner?.holes.push(hole);
  }
  const toV = ([x, y]) => new THREE.Vector2((x - W / 2) * MM, -(y - H / 2) * MM);
  return outers.map(({ loop, holes }) => {
    const shape = new THREE.Shape(loop.map(toV));
    for (const h of holes) shape.holes.push(new THREE.Path(h.map(toV)));
    return shape;
  });
}

/** Everything the 3D needs for one species: border and silhouette shapes, tie hole, and where the initials fit.
 *  With `slot`, the tie hole is a ribbon slot (RANURA) instead of the round ring hole. */
async function buildSpecies(src, slot = false) {
  const { W, H, grid: S } = rasterize(await inkFromSrc(src));
  const toS = distanceTo(S, W, H, 1);
  const B = new Uint8Array(W * H);
  for (let i = 0; i < B.length; i++) B[i] = toS[i] <= BORDE_PX ? 1 : 0;

  // The whole silhouette, before any hole is cut, for the round and square markers that carry it inside
  const wholeShapes = shapesFrom(S, W, H);
  let sx0 = W, sy0 = H, sx1 = 0, sy1 = 0;
  for (let i = 0; i < S.length; i++) if (S[i]) {
    const x = i % W, y = (i - x) / W;
    if (x < sx0) sx0 = x; if (x > sx1) sx1 = x; if (y < sy0) sy0 = y; if (y > sy1) sy1 = y;
  }
  const wholeBox = {
    x: ((sx0 + sx1 + 1) / 2 - W / 2) * MM,
    y: -((sy0 + sy1 + 1) / 2 - H / 2) * MM,
    w: (sx1 - sx0 + 1) * MM,
    h: (sy1 - sy0 + 1) * MM,
  };

  // Ring hole: as high as possible inside the body of the silhouette, never in the thin tips of
  // antlers or horns, where the ring would snap them
  const slotW = RANURA.w / 2 / MM, slotH = RANURA.h / 2 / MM;
  const holeR = (slot ? slotW : AGUJERO_MM / MM), wall = PARED_MM / MM;
  const inS = distanceTo(S, W, H, 0);
  let cx = 0, n = 0;
  for (let i = 0; i < S.length; i++) if (S[i]) { cx += i % W; n++; }
  cx /= n;
  let best = -1, bestScore = Infinity, deepest = 0;
  for (let i = 0; i < inS.length; i++) {
    if (inS[i] > inS[deepest]) deepest = i;
    if (inS[i] < holeR + wall) continue;
    const x = i % W, y = (i - x) / W;
    const score = y + 0.2 * Math.abs(x - cx);
    if (score < bestScore) { bestScore = score; best = i; }
  }
  if (best < 0) best = deepest;
  const hx = best % W, hy = (best - hx) / W;
  for (let y = Math.floor(hy - holeR - 1); y <= hy + holeR + 1; y++)
    for (let x = Math.floor(hx - holeR - 1); x <= hx + holeR + 1; x++) {
      const cut = slot ? Math.abs(x + 0.5 - hx) <= slotW && Math.abs(y + 0.5 - hy) <= slotH : Math.hypot(x + 0.5 - hx, y + 0.5 - hy) <= holeR;
      if (cut) { B[y * W + x] = 0; S[y * W + x] = 0; }
    }

  // The initials go where the silhouette is widest, now that the hole is cut
  const room = distanceTo(S, W, H, 0);
  let pole = 0;
  for (let i = 0; i < room.length; i++) if (room[i] > room[pole]) pole = i;
  const px = pole % W, py = (pole - px) / W;

  const mask = document.createElement('canvas');
  mask.width = W; mask.height = H;
  const mg = mask.getContext('2d');
  const md = mg.createImageData(W, H);
  for (let i = 0; i < S.length; i++) md.data[i * 4 + 3] = S[i] ? 255 : 0;
  mg.putImageData(md, 0, 0);

  let minX = W, minY = H, maxX = 0, maxY = 0;
  for (let i = 0; i < B.length; i++) if (B[i]) {
    const x = i % W, y = (i - x) / W;
    if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  const lowest = -(maxY + 1 - H / 2) * MM;

  return {
    W, H, mask,
    borderShapes: shapesFrom(B, W, H),
    silhouetteShapes: shapesFrom(S, W, H),
    hole: { x: (hx - W / 2) * MM, y: -(hy - H / 2) * MM },
    pole: { x: px, y: py, r: room[pole] },
    size: { w: (maxX - minX + 1) * MM, h: (maxY - minY + 1) * MM },
    lowest,
    wholeShapes,
    wholeBox,
  };
}

/* ------------------------------------------------------------------ 3D scene */

/** Species built from silhouette images, by image URL, shared by every viewer on the page. */
const speciesCache = /** @type {Map<string, Promise<any>>} */ (new Map());

/**
 * @param {string} src
 * @param {boolean} [slot] - Cut a ribbon slot instead of the ring hole.
 * @returns {Promise<any>}
 */
function speciesFrom(src, slot = false) {
  // A silhouette with a ribbon has a slot cut instead of the ring hole, so it is built apart
  const key = slot ? `${src}#ribbon` : src;
  let promise = speciesCache.get(key);
  if (!promise) {
    // The key identifies the species, so the scene knows when the markers shown have changed
    promise = buildSpecies(src, slot).then((data) => Object.assign(data, { id: key }));
    speciesCache.set(key, promise);
    promise.catch(() => speciesCache.delete(key));
  }
  return promise;
}

/** Planar UVs across the front of a piece, so painted stripes and rings line up on its face and sides. */
function planarUV(geometry, w, h) {
  const pos = geometry.attributes.position;
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2] = pos.getX(i) / w + 0.5;
    uv[i * 2 + 1] = pos.getY(i) / h + 0.5;
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

/** One color mask filled with a color. */
function tintedMask(mask, hex) {
  const c = document.createElement('canvas');
  c.width = mask.width;
  c.height = mask.height;
  const g = c.getContext('2d');
  if (!g) return c;
  g.drawImage(mask, 0, 0);
  g.globalCompositeOperation = 'source-in';
  g.fillStyle = hex;
  g.fillRect(0, 0, c.width, c.height);
  return c;
}

/** Writes text at the given size, shrinking it until it fits the width. Returns the size used, in canvas pixels. */
function fitText(g, text, x, y, maxW, size, family, weight = 400) {
  const font = (s) => `${weight} ${s}px ${family}`;
  g.font = font(size);
  const width = g.measureText(text).width;
  if (width > maxW) {
    size *= maxW / width;
    g.font = font(size);
  }
  g.fillText(text, x, y);
  return size;
}

/** Round and square (diamond) markers: outline, background, and where the animal and the initials go. */
function markerShapes() {
  const THREE = window.THREE;
  const slotPath = (y) => {
    const path = new THREE.Path();
    const w = RANURA.w / 2;
    const h = RANURA.h / 2;
    path.moveTo(-w, y - h);
    path.lineTo(w, y - h);
    path.lineTo(w, y + h);
    path.lineTo(-w, y + h);
    path.lineTo(-w, y - h);
    return path;
  };

  const R = 32.5;
  const rb = 7;
  const r = R - rb;
  const circle = (inset = 0) => {
    const s = new THREE.Shape();
    s.absarc(0, 0, R - inset, 0, Math.PI * 2, false);
    return s;
  };
  const roundSlot = R - rb / 2;

  const side = 52;
  const D = side / Math.SQRT2;
  const sb = 6;
  const Di = D - sb * Math.SQRT2;
  const diamond = (d, round = 3) => {
    const s = new THREE.Shape();
    const pts = [[0, d], [d, 0], [0, -d], [-d, 0]];
    const k = round / Math.SQRT2;
    s.moveTo(k, d - k);
    pts.forEach((p, i) => {
      const next = pts[(i + 1) % 4];
      const after = pts[(i + 2) % 4];
      s.lineTo(next[0] + (p[0] - next[0]) * (k / d), next[1] + (p[1] - next[1]) * (k / d));
      s.quadraticCurveTo(next[0], next[1], next[0] + (after[0] - next[0]) * (k / d), next[1] + (after[1] - next[1]) * (k / d));
    });
    return s;
  };
  // The background stops short of the top corner, which keeps room for the ribbon slot
  const cut = Di - 6.5;
  const squareSlot = D - 10;

  return {
    redondo: {
      extent: R,
      outer: circle,
      holes: [slotPath(roundSlot)],
      background: circle(rb),
      ringInsets: [rb / 3, (2 * rb) / 3],
      animal: { x: 0, y: 4.5, w: r * 1.55, h: r * 0.9 },
      text: { x: 0, y: -13.5, w: r * 1.2, h: 8.5 },
      slot: { x: 0, y: roundSlot },
    },
    cuadrado: {
      extent: D,
      outer: (inset = 0) => diamond(D - inset * Math.SQRT2),
      holes: [slotPath(squareSlot)],
      background: new THREE.Shape([
        new THREE.Vector2(-(Di - cut), cut),
        new THREE.Vector2(Di - cut, cut),
        new THREE.Vector2(Di, 0),
        new THREE.Vector2(0, -Di),
        new THREE.Vector2(-Di, 0),
      ]),
      ringInsets: [sb / 3, (2 * sb) / 3],
      animal: { x: 0, y: 3.5, w: 30, h: 19 },
      text: { x: 0, y: -12, w: 22, h: 7.5 },
      slot: { x: 0, y: squareSlot },
    },
  };
}

/**
 * @typedef {object} MarkerDesign
 * @property {MarkerLayout} layout
 * @property {any[]} items - Species data of every marker shown, in order.
 * @property {{ animal: string, background: string, border: string | null, mark: string, case: string, caseLetters: string }} colors - Hex colors; a null border is the flag.
 * @property {string} initials
 * @property {HTMLCanvasElement | null} logo - One color mask of the uploaded logo.
 * @property {string} caseInitials
 */

/**
 * 3D scene of the game markers: one hanging marker, or the set laid on the table with its case.
 */
class MarkerScene {
  #frame = 0;
  #resizeObserver;
  /** @type {MarkerDesign | null} */
  #design = null;
  #layoutKey = '';
  #silhouetteParts = new Map();
  #shapeParts = /** @type {Record<string, any>} */ ({});
  #shapes = markerShapes();
  #ribbonGeometry = null;
  #shortRibbonGeometry = null;
  #caseParts = null;
  #tubeParts = null;
  #viewDirection;
  #fonts;

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {HTMLElement} viewport
   * @param {{ engrave: string, display: string }} fonts - CSS font families for initials on silhouettes and on medals.
   */
  constructor(canvas, viewport, fonts) {
    const THREE = window.THREE;
    this.#fonts = fonts;
    this.canvas = canvas;
    this.viewport = viewport;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(32, 1, 1, 5000);
    this.controls = new THREE.OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.autoRotateSpeed = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0.6 : 2.2;
    this.#viewDirection = new THREE.Vector3(0, 0.04, 1);

    this.scene.add(new THREE.HemisphereLight(0xfff4e6, 0x30343c, 0.75));
    const key = new THREE.DirectionalLight(0xffffff, 1.2);
    key.position.set(120, 260, 220);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    Object.assign(key.shadow.camera, { left: -280, right: 280, top: 280, bottom: -280, near: 10, far: 1400 });
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0xffb27a, 0.5);
    rim.position.set(-200, 80, -160);
    this.scene.add(rim);

    this.floor = new THREE.Mesh(new THREE.PlaneGeometry(1600, 1600), new THREE.ShadowMaterial({ opacity: 0.28 }));
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.receiveShadow = true;
    this.scene.add(this.floor);
    this.display = new THREE.Group();
    this.scene.add(this.display);

    const pla = (hex) => new THREE.MeshStandardMaterial({ color: hex, roughness: 0.62, metalness: 0.02 });
    this.materials = {
      border: pla('#D2433D'),
      medalBorder: pla('#D2433D'),
      background: pla('#F4F1EC'),
      silhouette: pla('#F68A47'),
      case: pla('#B0936C'),
      ring: new THREE.MeshStandardMaterial({ color: '#c9ccd2', roughness: 0.28, metalness: 0.9 }),
    };

    // Silhouette markers: the flag runs straight across them
    const flag = document.createElement('canvas');
    flag.width = 8;
    flag.height = 256;
    const fg = flag.getContext('2d');
    if (fg) {
      fg.fillStyle = FLAG_HEX.Rojo;
      fg.fillRect(0, 0, 8, 256);
      fg.fillStyle = FLAG_HEX.Amarillo;
      fg.fillRect(0, 64, 8, 128);
    }
    this.flagTexture = this.#texture(flag);

    // The ribbon: red, yellow and red along its length, the yellow twice as wide, like the flag
    const stripes = document.createElement('canvas');
    stripes.width = 100;
    stripes.height = 8;
    const sg = stripes.getContext('2d');
    if (sg) {
      sg.fillStyle = FLAG_HEX.Rojo;
      sg.fillRect(0, 0, 100, 8);
      sg.fillStyle = FLAG_HEX.Amarillo;
      sg.fillRect(25, 0, 50, 8);
    }
    this.ribbonMaterial = new THREE.MeshStandardMaterial({ map: this.#texture(stripes), roughness: 0.8, side: THREE.DoubleSide });
    this.ringRadius = 8;
    this.ringGeometry = new THREE.TorusGeometry(this.ringRadius, 1.1, 12, 48);

    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(viewport);
    this.#resize();
    const loop = () => {
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
      this.#frame = requestAnimationFrame(loop);
    };
    loop();
  }

  /**
   * The markers as the customer sees them, as a picture for the order.
   * @returns {HTMLCanvasElement | null}
   */
  snapshot() {
    this.renderer.render(this.scene, this.camera);
    return pictureOf(this.renderer.domElement);
  }

  dispose() {
    cancelAnimationFrame(this.#frame);
    this.#resizeObserver.disconnect();
    this.controls.dispose();
    this.renderer.dispose();
  }

  get spinning() {
    return this.controls.autoRotate;
  }

  toggleSpin() {
    this.controls.autoRotate = !this.controls.autoRotate;
    return this.controls.autoRotate;
  }

  /**
   * @param {'front' | 'back'} view
   */
  goView(view) {
    const direction = this.#viewDirection.clone();
    if (view === 'back') {
      direction.x *= -1;
      direction.z *= -1;
    }
    const distance = this.camera.position.distanceTo(this.controls.target);
    this.camera.position.copy(this.controls.target).add(direction.normalize().multiplyScalar(distance));
    this.controls.update();
  }

  /**
   * @param {MarkerDesign} design
   */
  setDesign(design) {
    const previous = this.#design;
    this.#design = design;
    this.#updateMaterials();
    for (const [data, parts] of this.#silhouetteParts) this.#drawSilhouetteDecal(data, parts);
    for (const id of Object.keys(this.#shapeParts)) this.#drawShapeDecal(id);
    this.#drawCaseDecals();
    const key = JSON.stringify(design.layout) + design.items.map((item) => item.id).join(',');
    if (key !== this.#layoutKey || previous?.layout.shape !== design.layout.shape) {
      this.#layoutKey = key;
      this.#compose();
    }
  }

  /** Letter height in millimetres of the initials on the marker shown, or 0. */
  get letterMm() {
    const design = this.#design;
    if (!design) return 0;
    if (design.layout.shape === 'silueta') return this.#silhouetteParts.get(design.items[0])?.letterMm ?? 0;
    return this.#shapeParts[design.layout.shape]?.letterMm ?? 0;
  }

  #texture(canvas) {
    const texture = new window.THREE.CanvasTexture(canvas);
    texture.encoding = window.THREE.sRGBEncoding;
    texture.anisotropy = 4;
    return texture;
  }

  #resize() {
    const { clientWidth: w, clientHeight: h } = this.viewport;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  #updateMaterials() {
    const design = this.#design;
    if (!design) return;
    const { colors, layout } = design;
    const m = this.materials;
    m.silhouette.color.set(colors.animal).convertSRGBToLinear();
    m.background.color.set(colors.background).convertSRGBToLinear();
    m.case.color.set(colors.case).convertSRGBToLinear();
    const flag = colors.border === null;
    m.border.map = flag ? this.flagTexture : null;
    m.medalBorder.map = flag && layout.shape !== 'silueta' ? this.#shapePartsFor(layout.shape).ringsTexture : null;
    for (const material of [m.border, m.medalBorder]) {
      if (flag) material.color.set('#ffffff');
      else material.color.set(colors.border ?? DEFAULTS.border).convertSRGBToLinear();
      material.needsUpdate = true;
    }
  }

  /* -------- silhouette markers */

  #silhouettePartsFor(data) {
    let parts = this.#silhouetteParts.get(data);
    if (parts) return parts;
    const THREE = window.THREE;
    const fullW = data.W * MM;
    const fullH = data.H * MM;
    const border = new THREE.ExtrudeGeometry(data.borderShapes, { depth: BASE_MM, bevelEnabled: false, curveSegments: 1 });
    planarUV(border, fullW, fullH);
    const silhouette = new THREE.ExtrudeGeometry(data.silhouetteShapes, { depth: RELIEVE_MM, bevelEnabled: false, curveSegments: 1 });
    silhouette.translate(0, 0, BASE_MM);
    const plane = new THREE.PlaneGeometry(fullW, fullH);
    plane.translate(0, 0, BASE_MM + RELIEVE_MM + 0.02);
    const decalCanvas = document.createElement('canvas');
    decalCanvas.width = data.W * 3;
    decalCanvas.height = data.H * 3;
    const texture = this.#texture(decalCanvas);
    const decal = new THREE.MeshStandardMaterial({ map: texture, transparent: true, roughness: 0.55, polygonOffset: true, polygonOffsetFactor: -2 });
    parts = { border, silhouette, plane, decalCanvas, texture, decal, letterMm: 0 };
    this.#silhouetteParts.set(data, parts);
    this.#drawSilhouetteDecal(data, parts);
    return parts;
  }

  /** Initials or logo at the widest part of the silhouette, clipped to it. */
  #drawSilhouetteDecal(data, parts) {
    const design = this.#design;
    const c = parts.decalCanvas;
    const g = c.getContext('2d');
    if (!design || !g) return;
    const k = 3;
    g.clearRect(0, 0, c.width, c.height);
    const { x, y, r } = data.pole;
    parts.letterMm = 0;
    if (design.logo) {
      const box = r * 1.6 * k;
      const scale = Math.min(box / design.logo.width, box / design.logo.height);
      const w = design.logo.width * scale;
      const h = design.logo.height * scale;
      g.drawImage(tintedMask(design.logo, design.colors.mark), x * k - w / 2, y * k - h / 2, w, h);
    } else if (design.initials) {
      g.fillStyle = design.colors.mark;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      const size = fitText(g, design.initials, x * k, y * k + r * 0.05 * k, r * 1.75 * k, r * 1.15 * k, this.#fonts.engrave);
      parts.letterMm = (size / k) * 0.72 * MM;
    }
    // Nothing may stick out of the silhouette
    g.globalCompositeOperation = 'destination-in';
    g.drawImage(data.mask, 0, 0, c.width, c.height);
    g.globalCompositeOperation = 'source-over';
    parts.texture.needsUpdate = true;
  }

  /* -------- round and square markers */

  #shapePartsFor(id) {
    if (this.#shapeParts[id]) return this.#shapeParts[id];
    const THREE = window.THREE;
    const f = this.#shapes[id];
    const size = 2 * f.extent;
    const plateShape = f.outer();
    plateShape.holes.push(...f.holes);
    const plate = new THREE.ExtrudeGeometry(plateShape, { depth: BASE_MM, bevelEnabled: false, curveSegments: 48 });
    planarUV(plate, size, size);
    const background = new THREE.ExtrudeGeometry(f.background, { depth: 0.4, bevelEnabled: false, curveSegments: 48 });
    background.translate(0, 0, BASE_MM);

    // The flag border is three rings: red, yellow and red
    const rings = document.createElement('canvas');
    rings.width = rings.height = 512;
    const g = rings.getContext('2d');
    const toPx = (v) => (v / size + 0.5) * 512;
    const fill = (shape, hex) => {
      if (!g) return;
      g.beginPath();
      shape.getPoints(64).forEach((p, i) => (i ? g.lineTo(toPx(p.x), 512 - toPx(p.y)) : g.moveTo(toPx(p.x), 512 - toPx(p.y))));
      g.closePath();
      g.fillStyle = hex;
      g.fill();
    };
    fill(f.outer(), FLAG_HEX.Rojo);
    fill(f.outer(f.ringInsets[0]), FLAG_HEX.Amarillo);
    fill(f.outer(f.ringInsets[1]), FLAG_HEX.Rojo);

    const decalCanvas = document.createElement('canvas');
    decalCanvas.width = decalCanvas.height = 1024;
    const decalTexture = this.#texture(decalCanvas);
    const decal = new THREE.MeshStandardMaterial({ map: decalTexture, transparent: true, roughness: 0.55, polygonOffset: true, polygonOffsetFactor: -2 });
    const decalPlane = new THREE.PlaneGeometry(size, size);
    decalPlane.translate(0, 0, BASE_MM + 0.42);
    this.#shapeParts[id] = { plate, background, ringsTexture: this.#texture(rings), decalCanvas, decalTexture, decal, decalPlane, letterMm: 0, animals: new Map() };
    this.#drawShapeDecal(id);
    return this.#shapeParts[id];
  }

  /** Initials or logo under the animal of a round or square marker, kept inside its background. */
  #drawShapeDecal(id) {
    const parts = this.#shapeParts[id];
    const design = this.#design;
    if (!parts || !design) return;
    const f = this.#shapes[id];
    const c = parts.decalCanvas;
    const g = c.getContext('2d');
    if (!g) return;
    const size = 2 * f.extent;
    const px = (v) => (v / size + 0.5) * c.width;
    const py = (v) => (0.5 - v / size) * c.height;
    const perMm = c.width / size;
    g.clearRect(0, 0, c.width, c.height);
    parts.letterMm = 0;
    const area = f.text;
    if (design.logo) {
      const boxW = area.w * perMm;
      const boxH = area.h * 1.5 * perMm;
      const scale = Math.min(boxW / design.logo.width, boxH / design.logo.height);
      const w = design.logo.width * scale;
      const h = design.logo.height * scale;
      g.drawImage(tintedMask(design.logo, design.colors.mark), px(area.x) - w / 2, py(area.y) - h / 2, w, h);
    } else if (design.initials) {
      g.fillStyle = design.colors.mark;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      const used = fitText(g, design.initials, px(area.x), py(area.y), area.w * perMm, (area.h / 0.72) * perMm, this.#fonts.display, 700);
      parts.letterMm = (used / perMm) * 0.72;
    }
    g.globalCompositeOperation = 'destination-in';
    g.beginPath();
    f.background.getPoints(64).forEach((p, i) => (i ? g.lineTo(px(p.x), py(p.y)) : g.moveTo(px(p.x), py(p.y))));
    g.closePath();
    g.fill();
    g.globalCompositeOperation = 'source-over';
    parts.decalTexture.needsUpdate = true;
  }

  /** The animal, raised on the background of a round or square marker, scaled to its space. */
  #animalGeometry(id, data) {
    const parts = this.#shapePartsFor(id);
    if (parts.animals.has(data)) return parts.animals.get(data);
    const area = this.#shapes[id].animal;
    const geometry = new window.THREE.ExtrudeGeometry(data.wholeShapes, { depth: RELIEVE_MM, bevelEnabled: false, curveSegments: 1 });
    const scale = Math.min(area.w / data.wholeBox.w, area.h / data.wholeBox.h);
    geometry.translate(-data.wholeBox.x, -data.wholeBox.y, 0);
    geometry.scale(scale, scale, 1);
    geometry.translate(area.x, area.y, BASE_MM + 0.4);
    parts.animals.set(data, geometry);
    return geometry;
  }

  /**
   * The flag ribbon threaded through the slot: down the front, through, and up the back.
   * @param {boolean} [short] - A short loop for the markers of a set lying on the table, clear of the next row.
   */
  #ribbon(short = false) {
    const cached = short ? this.#shortRibbonGeometry : this.#ribbonGeometry;
    if (cached) return cached;
    const THREE = window.THREE;
    const points = short
      ? [[0, 12, 3.4], [0, 6, 3], [0, 0, 1], [0, 6, -1], [0, 11, -1.4]]
      : [[0, 46, 6], [0, 20, 4.6], [0, 6, 3.6], [0, 0, 1], [0, 6, -1.4], [0, 20, -2.8], [0, 43, -4.2]];
    const curve = new THREE.CatmullRomCurve3(points.map(([x, y, z]) => new THREE.Vector3(x, y, z)));
    const steps = 80;
    const half = 4.5;
    const positions = [];
    const uvs = [];
    const index = [];
    curve.getSpacedPoints(steps).forEach((p, i) => {
      positions.push(p.x - half, p.y, p.z, p.x + half, p.y, p.z);
      uvs.push(0, i / steps, 1, i / steps);
      if (i) {
        const a = (i - 1) * 2;
        index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
      }
    });
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(index);
    geometry.computeVertexNormals();
    if (short) this.#shortRibbonGeometry = geometry;
    else this.#ribbonGeometry = geometry;
    return geometry;
  }

  #addMeshes(group, pairs) {
    for (const [geometry, material, shadow = true] of pairs) {
      const mesh = new window.THREE.Mesh(geometry, material);
      mesh.castShadow = shadow;
      mesh.receiveShadow = true;
      group.add(mesh);
    }
  }

  /**
   * One marker of the chosen shape. With the flag ribbon option every marker carries its ribbon;
   * without it, a silhouette hanging on its own shows its ring.
   */
  #marker(data, hanging) {
    const THREE = window.THREE;
    const design = /** @type {MarkerDesign} */ (this.#design);
    const group = new THREE.Group();
    const shape = design.layout.shape;
    if (shape === 'silueta') {
      const parts = this.#silhouettePartsFor(data);
      this.#addMeshes(group, [[parts.border, this.materials.border], [parts.silhouette, this.materials.silhouette], [parts.plane, parts.decal, false]]);
      if (design.layout.ribbon) {
        // The ribbon goes through the slot cut in the silhouette
        const strip = new THREE.Mesh(this.#ribbon(!hanging), this.ribbonMaterial);
        strip.position.set(data.hole.x, data.hole.y, (BASE_MM + RELIEVE_MM - 2) / 2);
        strip.castShadow = true;
        group.add(strip);
      } else if (hanging) {
        const ring = new THREE.Mesh(this.ringGeometry, this.materials.ring);
        ring.rotation.y = Math.PI / 2;
        ring.position.set(data.hole.x, data.hole.y + this.ringRadius - AGUJERO_MM * 0.6, (BASE_MM + RELIEVE_MM) / 2);
        ring.castShadow = true;
        group.add(ring);
      }
      return group;
    }
    const parts = this.#shapePartsFor(shape);
    this.#addMeshes(group, [
      [parts.plate, this.materials.medalBorder],
      [parts.background, this.materials.background],
      [this.#animalGeometry(shape, data), this.materials.silhouette],
      [parts.decalPlane, parts.decal, false],
    ]);
    if (design.layout.ribbon) {
      const slot = this.#shapes[shape].slot;
      const strip = new THREE.Mesh(this.#ribbon(!hanging), this.ribbonMaterial);
      strip.position.set(slot.x, slot.y, 0);
      strip.castShadow = true;
      group.add(strip);
    }
    return group;
  }

  /* -------- cases: square box with a lid for silhouette and square markers, round tube for round ones */

  #lidDecal(size) {
    const decalCanvas = document.createElement('canvas');
    decalCanvas.width = decalCanvas.height = 640;
    const texture = this.#texture(decalCanvas);
    const material = new window.THREE.MeshStandardMaterial({ map: texture, transparent: true, roughness: 0.55, polygonOffset: true, polygonOffsetFactor: -2 });
    return { decalCanvas, texture, material, plane: new window.THREE.PlaneGeometry(size, size) };
  }

  #squareCase() {
    const THREE = window.THREE;
    if (!this.#caseParts) {
      const side = 86;
      const body = 30;
      const lid = 7;
      const radius = 9;
      const h = side / 2;
      const s = new THREE.Shape();
      s.moveTo(-h + radius, -h);
      s.lineTo(h - radius, -h);
      s.quadraticCurveTo(h, -h, h, -h + radius);
      s.lineTo(h, h - radius);
      s.quadraticCurveTo(h, h, h - radius, h);
      s.lineTo(-h + radius, h);
      s.quadraticCurveTo(-h, h, -h, h - radius);
      s.lineTo(-h, -h + radius);
      s.quadraticCurveTo(-h, -h, -h + radius, -h);
      const bevel = { bevelEnabled: true, bevelThickness: 1.2, bevelSize: 1.2, bevelSegments: 3, curveSegments: 10 };
      const bodyGeometry = new THREE.ExtrudeGeometry(s, { depth: body, ...bevel });
      const lidGeometry = new THREE.ExtrudeGeometry(s, { depth: lid, ...bevel });
      lidGeometry.translate(0, 0, body + 2.6);
      const decal = this.#lidDecal(side - 6);
      decal.plane.translate(0, 0, body + 2.6 + lid + 1.25);
      this.#caseParts = { body: bodyGeometry, lid: lidGeometry, depth: h, ...decal };
    }
    const parts = this.#caseParts;
    const group = new THREE.Group();
    this.#addMeshes(group, [[parts.body, this.materials.case], [parts.lid, this.materials.case], [parts.plane, parts.material, false]]);
    group.rotation.x = -Math.PI / 2;
    this.#drawCaseDecals();
    return { group, depth: parts.depth };
  }

  #roundCase() {
    const THREE = window.THREE;
    if (!this.#tubeParts) {
      const radius = 37;
      const height = 46;
      const lid = 6;
      const body = new THREE.CylinderGeometry(radius, radius, height, 72);
      body.translate(0, height / 2, 0);
      // Knurled screw lid, like the real tube
      const teeth = 64;
      const shape = new THREE.Shape();
      for (let i = 0; i <= teeth * 2; i++) {
        const a = (i / (teeth * 2)) * Math.PI * 2;
        const r = i % 2 ? radius + 1.2 : radius + 2.4;
        if (i) shape.lineTo(Math.cos(a) * r, Math.sin(a) * r);
        else shape.moveTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      const lidGeometry = new THREE.ExtrudeGeometry(shape, { depth: lid, bevelEnabled: false });
      lidGeometry.rotateX(-Math.PI / 2);
      lidGeometry.translate(0, height + 1, 0);
      const decal = this.#lidDecal(radius * 2);
      decal.plane.rotateX(-Math.PI / 2);
      decal.plane.translate(0, height + 1 + lid + 0.05, 0);
      this.#tubeParts = { body, lid: lidGeometry, depth: radius + 2.4, ...decal };
    }
    const parts = this.#tubeParts;
    const group = new THREE.Group();
    this.#addMeshes(group, [[parts.body, this.materials.case], [parts.lid, this.materials.case], [parts.plane, parts.material, false]]);
    this.#drawCaseDecals();
    return { group, depth: parts.depth };
  }

  /** Lid artwork: initials and MARCADORES on the square case; the animal and the initials on the round one. */
  #drawCaseDecals() {
    const design = this.#design;
    if (!design) return;
    const color = design.colors.caseLetters;
    const text = design.caseInitials;
    if (this.#caseParts) {
      const c = this.#caseParts.decalCanvas;
      const g = c.getContext('2d');
      if (g) {
        g.clearRect(0, 0, c.width, c.height);
        g.fillStyle = color;
        g.textAlign = 'center';
        g.textBaseline = 'middle';
        if (text) fitText(g, text, c.width / 2, c.height * 0.44, c.width * 0.8, 190, this.#fonts.engrave);
        g.font = `600 52px ${this.#fonts.display}`;
        g.fillText('MARCADORES', c.width / 2, text ? c.height * 0.72 : c.height / 2);
        this.#caseParts.texture.needsUpdate = true;
      }
    }
    if (this.#tubeParts) {
      const c = this.#tubeParts.decalCanvas;
      const g = c.getContext('2d');
      if (g) {
        g.clearRect(0, 0, c.width, c.height);
        const data = design.items[0];
        if (data) {
          const box = { w: c.width * 0.62, h: c.height * 0.36 };
          const scale = Math.min(box.w / data.mask.width, box.h / data.mask.height);
          const w = data.mask.width * scale;
          const h = data.mask.height * scale;
          g.drawImage(tintedMask(data.mask, color), (c.width - w) / 2, c.height * 0.38 - h / 2, w, h);
        }
        g.fillStyle = color;
        g.textAlign = 'center';
        g.textBaseline = 'middle';
        if (text) fitText(g, text, c.width / 2, c.height * 0.7, c.width * 0.6, 120, this.#fonts.display, 700);
        // Only the round lid is painted
        g.globalCompositeOperation = 'destination-in';
        g.beginPath();
        g.arc(c.width / 2, c.height / 2, c.width / 2, 0, Math.PI * 2);
        g.fill();
        g.globalCompositeOperation = 'source-over';
        this.#tubeParts.texture.needsUpdate = true;
      }
    }
  }

  /* -------- layout and camera */

  /** Lays out what the customer is buying: one marker hanging, or the set flat on the table with its case. */
  #compose() {
    const THREE = window.THREE;
    const design = this.#design;
    if (!design) return;
    this.display.clear();
    const { shape, quantity, withCase } = design.layout;
    const single = quantity === 1;
    if (single) {
      const data = design.items[0];
      if (!data) return;
      this.display.add(this.#marker(data, true));
      this.floor.position.y = (shape === 'silueta' ? data.lowest : -this.#shapes[shape].extent) - 14;
      this.#viewDirection = new THREE.Vector3(0, 0.04, 1);
    } else {
      const step = shape === 'silueta' ? 82 : 2 * this.#shapes[shape].extent + 8;
      const perRow = 5;
      const gapZ = step + 2;
      const count = design.items.length;
      const rows = Math.max(1, Math.ceil(count / perRow));
      design.items.forEach((data, i) => {
        const row = Math.floor(i / perRow);
        const col = i % perRow;
        const inRow = Math.min(perRow, count - row * perRow);
        const marker = this.#marker(data, false);
        marker.rotation.x = -Math.PI / 2;
        marker.position.set((col - (inRow - 1) / 2) * step, 0, (row - (rows - 1) / 2) * gapZ);
        this.display.add(marker);
      });
      if (withCase) {
        const { group, depth } = shape === 'redondo' ? this.#roundCase() : this.#squareCase();
        group.position.set(0, 0, -((rows - 1) / 2) * gapZ - gapZ / 2 - depth - 10);
        this.display.add(group);
      }
      this.floor.position.y = -0.05;
      this.#viewDirection = new THREE.Vector3(0, 1.4, 1);
    }
    // A set on the table is wide and flat: its bounding sphere leaves room to come closer
    this.#fitCamera(single ? 0.92 : 0.7, single ? 0.08 : 0);
  }

  /**
   * @param {number} closeness
   * @param {number} lift - Raises the piece in the frame, clear of the controls along the bottom.
   */
  #fitCamera(closeness, lift) {
    const THREE = window.THREE;
    const box = new THREE.Box3().setFromObject(this.display);
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3()).add(new THREE.Vector3(0, -lift * size.y, 0));
    const radius = size.length() / 2;
    const fov = THREE.MathUtils.degToRad(this.camera.fov);
    const horizontal = 2 * Math.atan(Math.tan(fov / 2) * this.camera.aspect);
    const fit = Math.min(fov, horizontal);
    // Never closer than what keeps the whole width in view, with a little margin
    const widthDistance = (size.x * 0.55) / Math.tan(horizontal / 2) + size.z / 2;
    const distance = Math.max((radius / Math.sin(fit / 2)) * closeness, widthDistance);
    this.controls.target.copy(center);
    this.camera.position.copy(center).add(this.#viewDirection.clone().normalize().multiplyScalar(distance));
    this.controls.minDistance = distance * 0.3;
    this.controls.maxDistance = distance * 2.5;
    this.controls.update();
  }
}

/* ------------------------------------------------------------------ components */

/**
 * @typedef {object} MarcadorViewerRefs
 * @property {HTMLElement} viewport - Hosts the canvas.
 * @property {HTMLCanvasElement} canvas - WebGL canvas.
 * @property {HTMLButtonElement} spinButton - Toggles auto rotation.
 * @property {HTMLButtonElement[]} viewButtons - Front and back views.
 * @property {HTMLButtonElement[]} modeButtons - Switch between the 3D preview and the product photos.
 * @property {HTMLElement} error - Message shown when the viewer cannot start.
 * @property {HTMLScriptElement} animals - JSON list of the animals and their silhouette images.
 * @property {HTMLInputElement} [picture] - File field that sends a picture of the design with the order.
 */

/**
 * Interactive 3D preview of the game markers. It mirrors the selected variant (shape and quantity)
 * and the personalization fields without owning any form data.
 * @extends Component<MarcadorViewerRefs>
 */
class MarcadorViewer extends Component {
  requiredRefs = ['viewport', 'canvas', 'spinButton', 'error', 'animals'];

  /** @type {MarkerScene | null} */
  #scene = null;
  /** @type {AbortController | null} */
  #abortController = null;
  #started = false;
  #dismissed = false;
  #inView = true;
  /** @type {IntersectionObserver | null} */
  #floatObserver = null;
  /** Each sync waits for silhouettes to load; only the latest one may update the scene. */
  #syncId = 0;
  /** Object URLs of uploaded images, by file. */
  #urls = new Map();
  /** One color masks of uploaded logos, by file. */
  #logos = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const root = this.closest('.shopify-section') ?? document;
    const onFieldEvent = (/** @type {Event} */ event) => this.#handleFieldEvent(event);
    root.addEventListener('change', onFieldEvent, { signal });
    root.addEventListener('input', onFieldEvent, { signal });
    root.addEventListener('focusin', onFieldEvent, { signal });
    DESKTOP_QUERY.addEventListener('change', () => this.#updateFloating(), { signal });
    // The picture must be in the form before the cart reads it: the buy button is pressed before the
    // form is submitted (Enter in a field presses it too), and the theme reads the form on submit
    document.addEventListener('click', (event) => this.#attachPicture(event), { signal, capture: true });
    // Express checkout asks for the picture, and says when it has read the form
    document.addEventListener('personalization:picture', (event) => this.#attachPicture(event), { signal, capture: true });
    document.addEventListener('personalization:picture-sent', (event) => this.#clearPicture(event), { signal, capture: true });
    this.#floatObserver = new IntersectionObserver(([entry]) => {
      this.#inView = entry?.isIntersecting ?? true;
      this.#updateFloating();
    });
    this.#floatObserver.observe(this);
    if (this.dataset.mode === '3d') this.#ensureStarted();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#floatObserver?.disconnect();
    this.#floatObserver = null;
    this.#scene?.dispose();
    this.#scene = null;
    this.#started = false;
    for (const url of this.#urls.values()) URL.revokeObjectURL(url);
    this.#urls.clear();
    this.#logos.clear();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  /**
   * @param {Event} event
   */
  showMode(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-mode]') : null;
    if (!(button instanceof HTMLElement)) return;
    if (button.dataset.mode === '3d') this.#dismissed = false;
    this.#setMode(button.dataset.mode === '3d' ? '3d' : 'photos');
  }

  /**
   * @param {Event} event
   */
  showView(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-view]') : null;
    if (!(button instanceof HTMLElement) || !this.#scene) return;
    const view = button.dataset.view === 'back' ? 'back' : 'front';
    this.#scene.goView(view);
    for (const viewButton of this.refs.viewButtons ?? []) {
      viewButton.setAttribute('aria-pressed', String(viewButton === button));
    }
  }

  toggleSpin() {
    if (!this.#scene) return;
    this.refs.spinButton.setAttribute('aria-pressed', String(this.#scene.toggleSpin()));
  }

  /**
   * Sends a picture of the design with the order, so the workshop sees what the customer saw.
   * Only when the 3D preview was opened; otherwise the order goes without it.
   * @param {Event} event
   */
  #attachPicture(event) {
    const input = this.refs.picture;
    const form = getForm(this.dataset.formId);
    if (!input || !form) return;
    if (event.type === 'click') {
      const button = event.target instanceof Element ? event.target.closest('button[type="submit"], input[type="submit"]') : null;
      if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement) || button.form !== form) return;
      // The cart reads the form in the submit that follows this click; afterwards the picture is
      // removed, so a later express checkout never sends it as a bare file name
      setTimeout(() => (input.value = ''), 0);
    } else if (event.target !== form) {
      return;
    }
    const picture = this.#scene?.snapshot();
    if (picture) attachPicture(picture, input);
  }

  /**
   * Removes the picture once express checkout has read the form.
   * @param {Event} event
   */
  #clearPicture(event) {
    const input = this.refs.picture;
    if (input && event.target === getForm(this.dataset.formId)) input.value = '';
  }

  /** Hides the floating preview; it stays closed until the customer picks 3D again. */
  dismissFloating() {
    this.#dismissed = true;
    // Without product photos there is no mode to go back to: the 3D stays in its place in the page
    if (this.refs.modeButtons?.length) this.#setMode('photos');
    else this.#updateFloating();
  }

  /**
   * @param {Event} event
   */
  #handleFieldEvent(event) {
    if (event.type !== 'focusin') this.#sync();
    const target = event.target;
    // Compare form elements: the product form has an input named "id", which shadows `form.id`
    const isPersonalizationField =
      (target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement) &&
      target.name.startsWith('properties[') &&
      target.form !== null &&
      target.form === getForm(this.dataset.formId);
    if (isPersonalizationField && this.dataset.mode !== '3d' && !this.#dismissed) this.#setMode('3d');
  }

  /**
   * @param {'3d' | 'photos'} mode
   */
  #setMode(mode) {
    this.dataset.mode = mode;
    for (const modeButton of this.refs.modeButtons ?? []) {
      modeButton.setAttribute('aria-pressed', String(modeButton.dataset.mode === mode));
    }
    if (mode === '3d') this.#ensureStarted();
    this.#updateFloating();
  }

  /** Floats the 3D stage at the top of a mobile screen while its place in the page is scrolled away. */
  #updateFloating() {
    const float = !this.#inView && this.dataset.mode === '3d' && !this.#dismissed && !DESKTOP_QUERY.matches;
    if (float === this.hasAttribute('data-floating')) return;
    // Keep the space the stage leaves behind so the page does not jump
    if (float) this.style.setProperty('--marcador-3d-placeholder', `${this.offsetHeight}px`);
    this.toggleAttribute('data-floating', float);
    setScrollOffset(float ? this.querySelector('.marcador-3d__stage') : null);
  }

  #ensureStarted() {
    if (this.#started || !this.#abortController) return;
    this.#started = true;
    this.#start(this.#abortController.signal);
  }

  /**
   * @param {AbortSignal} signal
   */
  async #start(signal) {
    const { threeSrc = '', controlsSrc = '', engraveFontSrc = '' } = this.dataset;
    try {
      await loadThree(threeSrc, controlsSrc);
    } catch {
      if (!signal.aborted) this.refs.error.hidden = false;
      return;
    }
    if (signal.aborted) return;

    const display = getComputedStyle(this).getPropertyValue('--font-heading--family').trim() || 'sans-serif';
    if (engraveFontSrc && 'FontFace' in window) {
      try {
        const face = new FontFace('Marcador Cooper', `url(${engraveFontSrc})`);
        document.fonts.add(await face.load());
      } catch {
        // The initials fall back to the heading font
      }
    }
    if (signal.aborted) return;
    try {
      this.#scene = new MarkerScene(this.refs.canvas, this.refs.viewport, { engrave: `"Marcador Cooper", ${display}`, display });
    } catch {
      this.refs.error.hidden = false;
      return;
    }
    await this.#sync();
  }

  /**
   * @returns {Array<{ name: string, src: string }>}
   */
  #animals() {
    try {
      const list = JSON.parse(this.refs.animals.textContent ?? '[]');
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {File} file
   */
  #urlFor(file) {
    let url = this.#urls.get(file);
    if (!url) {
      for (const old of this.#urls.values()) URL.revokeObjectURL(old);
      this.#urls.clear();
      url = URL.createObjectURL(file);
      this.#urls.set(file, url);
    }
    return url;
  }

  /**
   * @param {File | null} file
   * @returns {Promise<HTMLCanvasElement | null>}
   */
  async #logoFor(file) {
    if (!file) return null;
    if (this.#logos.has(file)) return this.#logos.get(file);
    const url = URL.createObjectURL(file);
    let mask = null;
    try {
      const { canvas, bbox } = await inkFromSrc(url, 800);
      mask = document.createElement('canvas');
      mask.width = bbox.w;
      mask.height = bbox.h;
      mask.getContext('2d')?.drawImage(canvas, bbox.x, bbox.y, bbox.w, bbox.h, 0, 0, bbox.w, bbox.h);
    } catch {
      mask = null;
    }
    URL.revokeObjectURL(url);
    this.#logos.clear();
    this.#logos.set(file, mask);
    return mask;
  }

  /** Reads the variant and the personalization fields and updates the scene. */
  async #sync() {
    const scene = this.#scene;
    if (!scene) return;
    const id = ++this.#syncId;
    const form = getForm(this.dataset.formId);
    const layout = readLayout(this.closest('.shopify-section') ?? document);
    const animals = this.#animals();
    const custom = readFile(form, PROPERTIES.silhouette);

    // Which animal each marker carries
    let names = [];
    if (layout.quantity === 1) {
      names = [readValue(form, PROPERTIES.animal) || animals[0]?.name || ''];
    } else {
      names = parseSplit(readValue(form, PROPERTIES.split)).flatMap(([name, count]) => Array(count).fill(name));
      if (!names.length) names = Array(layout.quantity).fill(readValue(form, PROPERTIES.animal) || animals[0]?.name || '');
    }
    const srcFor = (/** @type {string} */ name) => {
      if (CUSTOM_ANIMAL.test(name)) return custom ? this.#urlFor(custom) : animals[0]?.src;
      return animals.find((animal) => animalKey(animal.name) === animalKey(name))?.src ?? animals[0]?.src;
    };

    let items;
    let logo;
    try {
      items = await Promise.all(names.map((name) => srcFor(name)).filter(Boolean).map((src) => speciesFrom(/** @type {string} */ (src), layout.ribbon && layout.shape === 'silueta')));
      logo = await this.#logoFor(readFile(form, PROPERTIES.logo));
    } catch {
      return;
    }
    if (id !== this.#syncId || !this.#scene) return;

    const swatch = (/** @type {string} */ key, /** @type {string} */ fallback) => readSwatchColor(form, key) || fallback;
    scene.setDesign({
      layout,
      items,
      colors: {
        animal: swatch(PROPERTIES.animalColor, DEFAULTS.animal),
        background: swatch(PROPERTIES.background, DEFAULTS.background),
        border: FLAG_BORDER.test(readValue(form, PROPERTIES.border) || 'Bandera') ? null : swatch(PROPERTIES.borderColor, DEFAULTS.border),
        mark: swatch(PROPERTIES.markColor, DEFAULTS.mark),
        case: swatch(PROPERTIES.caseColor, DEFAULTS.case),
        caseLetters: swatch(PROPERTIES.caseLettersColor, DEFAULTS.caseLetters),
      },
      initials: logo ? '' : readValue(form, PROPERTIES.initials) || (getControls(form, PROPERTIES.initials).length ? '' : SAMPLE_INITIALS),
      logo,
      caseInitials: readValue(form, PROPERTIES.caseInitials),
    });
  }
}

/**
 * @typedef {object} MarcadorOptionsRefs
 * @property {HTMLElement} split - Wraps the split of animals across several markers.
 * @property {HTMLElement} splitRows - One stepper per animal.
 * @property {HTMLElement} splitTotal - "3 de 5".
 * @property {HTMLElement} splitNotice - Says how many markers are still to be assigned.
 * @property {HTMLInputElement} splitInput - Line item property that carries the split.
 * @property {HTMLElement} colorsCount - How many filament colors each marker uses.
 */

/**
 * Applies the rules of the game marker form: one animal or a split across the markers bought,
 * fields that only apply to some shapes or quantities, the customer's own silhouette, and the
 * limit of filament colors per marker.
 * @extends Component<MarcadorOptionsRefs>
 */
class MarcadorOptions extends Component {
  requiredRefs = ['split', 'splitRows', 'splitTotal', 'splitNotice', 'splitInput', 'colorsCount'];

  /** @type {AbortController | null} */
  #abortController = null;
  /** Markers per animal while several are bought. */
  #split = /** @type {Map<string, number>} */ (new Map());
  #splitQuantity = 0;
  #rowsKey = '';

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const root = this.closest('.shopify-section') ?? document;
    root.addEventListener('change', (event) => this.#handleChange(event), { signal });
    root.addEventListener('input', (event) => this.#handleChange(event), { signal });
    this.#selectDefaultBorder();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#rowsKey = '';
    this.#selectDefaultBorder();
    this.#update();
  }

  /** The border starts as the flag, the usual choice, instead of an empty list. */
  #selectDefaultBorder() {
    const select = getControls(getForm(this.dataset.formId), PROPERTIES.border)[0];
    if (!(select instanceof HTMLSelectElement) || select.value) return;
    const flag = [...select.options].find((option) => FLAG_BORDER.test(option.value));
    if (!flag) return;
    select.value = flag.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {Event} event
   */
  changeCount(event) {
    const button = event.target instanceof Element ? event.target.closest('button[data-animal]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const name = button.dataset.animal ?? '';
    const delta = Number(button.dataset.delta) || 0;
    this.#split.set(name, Math.max(0, (this.#split.get(name) ?? 0) + delta));
    this.#update();
  }

  /**
   * @param {Event} event
   */
  #handleChange(event) {
    const target = event.target;
    if (target === this.refs.splitInput) return;
    if (target instanceof HTMLInputElement && target.dataset.splitAnimal) {
      this.#split.set(target.dataset.splitAnimal, Math.max(0, Math.round(Number(target.value) || 0)));
    }
    this.#update();
  }

  /** Animals offered in the Animal field. */
  #animalNames(form) {
    const select = getControls(form, PROPERTIES.animal)[0];
    if (!(select instanceof HTMLSelectElement)) return [];
    return [...select.options].map((option) => option.value).filter(Boolean);
  }

  #update() {
    const form = getForm(this.dataset.formId);
    if (!form) return;
    const layout = readLayout(this.closest('.shopify-section') ?? document);
    const several = layout.quantity > 1;
    const names = this.#animalNames(form);
    const chosen = readValue(form, PROPERTIES.animal);

    this.#showField(form, PROPERTIES.animal, !several);
    this.refs.split.hidden = !several;
    this.refs.splitInput.disabled = !several;

    let usesCustom = CUSTOM_ANIMAL.test(chosen);
    if (several) {
      // A new quantity starts with every marker of the animal chosen so far
      if (layout.quantity !== this.#splitQuantity) {
        this.#split = new Map([[chosen || names[0] || '', layout.quantity]]);
        this.#splitQuantity = layout.quantity;
      }
      this.#renderSplit(names, layout.quantity);
      usesCustom = [...this.#split].some(([name, count]) => count > 0 && CUSTOM_ANIMAL.test(name));
    } else {
      this.#splitQuantity = 0;
    }

    // The customer's own silhouette is asked for only when "Otra" is chosen
    this.#showField(form, PROPERTIES.silhouette, usesCustom);
    const silhouette = getControls(form, PROPERTIES.silhouette)[0];
    if (silhouette instanceof HTMLInputElement) {
      const missing = usesCustom && !readFile(form, PROPERTIES.silhouette);
      silhouette.setCustomValidity(missing ? (this.dataset.silhouetteMissing ?? '') : '');
    }

    this.#showField(form, PROPERTIES.background, layout.shape !== 'silueta');
    this.#showField(form, PROPERTIES.borderColor, !FLAG_BORDER.test(readValue(form, PROPERTIES.border) || 'Bandera'));
    const marked = Boolean(readValue(form, PROPERTIES.initials) || readFile(form, PROPERTIES.logo));
    this.#showField(form, PROPERTIES.markColor, marked);
    for (const key of [PROPERTIES.caseColor, PROPERTIES.caseInitials, PROPERTIES.caseLettersColor]) {
      this.#showField(form, key, layout.withCase);
    }

    this.#checkColors(form, layout);
  }

  /**
   * Shows or hides a personalization field; hidden fields are disabled so they are neither
   * required nor sent with the order.
   * @param {HTMLFormElement} form
   * @param {string} key
   * @param {boolean} shown
   */
  #showField(form, key, shown) {
    const controls = getControls(form, key);
    const wrapper = controls[0]?.closest('product-custom-property-component');
    if (wrapper instanceof HTMLElement && wrapper.hidden === shown) wrapper.hidden = !shown;
    for (const control of controls) {
      if (control.disabled === shown) control.disabled = !shown;
    }
  }

  /**
   * @param {string[]} names
   * @param {number} quantity
   */
  #renderSplit(names, quantity) {
    for (const name of [...this.#split.keys()]) {
      if (!names.includes(name)) this.#split.delete(name);
    }
    const total = [...this.#split.values()].reduce((sum, count) => sum + count, 0);
    const rowsKey = names.join('|');
    if (rowsKey !== this.#rowsKey) {
      this.#rowsKey = rowsKey;
      this.refs.splitRows.replaceChildren(
        ...names.map((name, index) => {
          const row = document.createElement('div');
          row.className = 'marcador-opciones__row';
          const id = `${this.dataset.idPrefix ?? 'marcador'}-${index}`;
          const label = document.createElement('label');
          label.htmlFor = id;
          label.textContent = name;
          const stepper = document.createElement('div');
          stepper.className = 'marcador-opciones__stepper';
          const button = (/** @type {number} */ delta, /** @type {string} */ text, /** @type {string} */ template) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.textContent = text;
            b.dataset.animal = name;
            b.dataset.delta = String(delta);
            b.setAttribute('aria-label', template.replace('[name]', name));
            b.addEventListener('click', (event) => this.changeCount(event));
            return b;
          };
          const input = document.createElement('input');
          input.type = 'number';
          input.id = id;
          input.min = '0';
          input.inputMode = 'numeric';
          input.dataset.splitAnimal = name;
          // Linked to the product form so an incomplete split stops the purchase; it has no name, so it is not sent
          if (this.dataset.formId) input.setAttribute('form', this.dataset.formId);
          stepper.append(button(-1, '−', this.dataset.removeLabel ?? '[name]'), input, button(1, '+', this.dataset.addLabel ?? '[name]'));
          row.append(label, stepper);
          return row;
        })
      );
    }

    for (const input of this.refs.splitRows.querySelectorAll('input[data-split-animal]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const count = this.#split.get(input.dataset.splitAnimal ?? '') ?? 0;
      if (document.activeElement !== input) input.value = String(count);
      input.max = String(quantity);
      const row = input.closest('.marcador-opciones__row');
      const [minus, plus] = row ? [...row.querySelectorAll('button')] : [];
      if (minus) minus.disabled = count === 0;
      if (plus) plus.disabled = total >= quantity;
    }

    this.refs.splitTotal.textContent = (this.dataset.splitCount ?? '[count]/[total]').replace('[count]', String(total)).replace('[total]', String(quantity));
    this.refs.splitTotal.toggleAttribute('data-off', total !== quantity);
    this.refs.splitNotice.textContent =
      total < quantity ? (this.dataset.splitMissing ?? '').replace('[count]', String(quantity - total)) : '';

    const first = this.refs.splitRows.querySelector('input[data-split-animal]');
    if (first instanceof HTMLInputElement) {
      first.setCustomValidity(total === quantity ? '' : (this.dataset.splitInvalid ?? '').replace('[total]', String(quantity)));
    }

    const value = [...this.#split]
      .filter(([, count]) => count > 0)
      .map(([name, count]) => `${count} ${name}`)
      .join(', ');
    if (this.refs.splitInput.value !== value) {
      this.refs.splitInput.value = value;
      // Lets the 3D preview follow the new split
      this.refs.splitInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {MarkerLayout} layout
   */
  #checkColors(form, layout) {
    const used = colorsUsed(form, layout);
    const max = Number(this.dataset.max) || MAX_COLORS;
    const over = used.length > max;
    const text = (this.dataset.colorsUsed ?? '[count]/[max]').replace('[count]', String(used.length)).replace('[max]', String(max));
    if (this.refs.colorsCount.textContent !== text) this.refs.colorsCount.textContent = text;
    this.refs.colorsCount.toggleAttribute('data-off', over);

    // The order cannot be added while a marker needs more colors than the printer takes
    const message = over ? (this.dataset.colorsOver ?? '').replace('[max]', String(max)) : '';
    const anchors = [PROPERTIES.markColor, PROPERTIES.borderColor, PROPERTIES.animalColor]
      .map((key) => getControls(form, key)[0])
      .filter((control) => control && !control.disabled);
    for (const key of [PROPERTIES.markColor, PROPERTIES.borderColor, PROPERTIES.animalColor]) {
      for (const control of getControls(form, key)) control.setCustomValidity('');
    }
    anchors[0]?.setCustomValidity(message);
  }
}

if (!customElements.get('marcador-3d-viewer')) customElements.define('marcador-3d-viewer', MarcadorViewer);
if (!customElements.get('marcador-opciones')) customElements.define('marcador-opciones', MarcadorOptions);
